import { useEffect, useMemo, useState } from "react";
import { useStore } from "../lib/store";
import type { Conversation, User } from "@swift/shared";

/**
 * @mention autocomplete-popover die onder de cursor verschijnt zodra je
 * `@x` typt. De Composer rapporteert via props of de trigger actief is
 * en welke query erna staat; deze component berekent de suggesties,
 * tekent de lijst, en levert via `onPick` de gekozen username (zonder @)
 * of een speciale `here`/`channel`-broadcast. Pijl-omhoog/omlaag/Enter
 * worden door de Composer doorgegeven via `selectedIndex` + `onMove` —
 * zo blijft toetsenbordfocus in het textarea liggen.
 *
 * Eén component, geen popper.js: positie absoluut, vlak boven de
 * composer. Compacte stijl, scrollbaar bij meer dan ~6 suggesties.
 */

export type MentionCandidate =
  | { kind: "user"; user: User }
  | { kind: "broadcast"; token: "here" | "channel"; label: string };

type Props = {
  conversation: Conversation;
  query: string; // tekst na de @, lowercased; lege string mag (toon alle leden)
  selectedIndex: number;
  onPick(candidate: MentionCandidate): void;
  onCandidatesChanged(list: MentionCandidate[]): void;
};

const MAX_SUGGESTIONS = 6;

export default function MentionAutocomplete({
  conversation,
  query,
  selectedIndex,
  onPick,
  onCandidatesChanged,
}: Props) {
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const candidates = useMemo<MentionCandidate[]>(() => {
    const memberIds = new Set(conversation.member_ids);
    const list: MentionCandidate[] = [];

    // Broadcast-tokens alleen in kanalen — in een DM zou @here onzinnig
    // zijn ("hier" is alleen jij + de ander).
    if (conversation.kind === "channel") {
      const hereLabel = "Iedereen die nu online is in dit kanaal";
      const channelLabel = "Alle leden van dit kanaal";
      if ("here".startsWith(query)) {
        list.push({ kind: "broadcast", token: "here", label: hereLabel });
      }
      if ("channel".startsWith(query)) {
        list.push({ kind: "broadcast", token: "channel", label: channelLabel });
      }
    }

    const matches = Object.values(users)
      .filter((u) => u.id !== me?.id && memberIds.has(u.id))
      .filter((u) => {
        if (!query) return true;
        return (
          u.username.toLowerCase().startsWith(query) ||
          u.display_name.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.display_name.localeCompare(b.display_name))
      .slice(0, MAX_SUGGESTIONS)
      .map((u) => ({ kind: "user" as const, user: u }));

    return [...list, ...matches].slice(0, MAX_SUGGESTIONS);
  }, [users, me?.id, conversation.member_ids, conversation.kind, query]);

  useEffect(() => {
    onCandidatesChanged(candidates);
  }, [candidates, onCandidatesChanged]);

  if (candidates.length === 0) return null;

  return (
    <div className="absolute bottom-full left-3 right-3 mb-1 max-h-60 overflow-y-auto rounded-md border border-slate-700 bg-slate-800 shadow-lg">
      {candidates.map((c, i) => {
        const isSelected = i === selectedIndex || i === hoverIndex;
        const key = c.kind === "user" ? `u:${c.user.id}` : `b:${c.token}`;
        return (
          <button
            key={key}
            type="button"
            onMouseEnter={() => setHoverIndex(i)}
            onMouseLeave={() => setHoverIndex(null)}
            onClick={() => onPick(c)}
            // mousedown vóór blur — anders sluit Composer de popover
            // voordat de klik geregistreerd is.
            onMouseDown={(e) => e.preventDefault()}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
              isSelected ? "bg-slate-700 text-slate-100" : "text-slate-300"
            }`}
          >
            {c.kind === "user" ? (
              <>
                <span className="text-purple-400">@{c.user.username}</span>
                <span className="truncate text-slate-400">{c.user.display_name}</span>
              </>
            ) : (
              <>
                <span className="text-purple-400">@{c.token}</span>
                <span className="truncate text-slate-400">{c.label}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
